import {
	DEFAULT_SEPARATOR_ICON,
	useBreadcrumbsState,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { memo } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Flex, FlexProps, Text, TextProps } from '@mantine/core';

interface Props {
	leftMargin?: string;
	breadcrumbTextVariant?: TextProps['variant'];
	fontWeight?: TextProps['fw'];
	activeFontWeight?: TextProps['fw'];
	textColor?: string;
	activeItemColor?: string;
}

const Breadcrumbs = ({
	breadcrumbTextVariant = 'h6',
	fontWeight = 400,
	activeFontWeight = 600,
	textColor,
	activeItemColor,
}: Props) => {
	const { breadcrumbs, separator } = useBreadcrumbsState();
	const visibleBreadcrumbs = breadcrumbs?.filter((breadcrumb) => !breadcrumb.hideInPageHeader);

	return (
		<BreadcrumbsWrapper
			align="center"
			$textColor={textColor}
			$activeItemColor={activeItemColor}
		>
			{visibleBreadcrumbs?.map((breadcrumb, index) => {
				const isFirstBreadcrumb = index === 0;
				const isLastBreadcrumb = index === (visibleBreadcrumbs?.length ?? 0) - 1;
				const breadcrumbName = (
					<Text
						variant={breadcrumbTextVariant}
						fw={isLastBreadcrumb ? activeFontWeight : fontWeight}
						lineClamp={1}
						truncate
						title={isFirstBreadcrumb ? undefined : breadcrumb.name}
						style={isFirstBreadcrumb ? { whiteSpace: 'nowrap' } : {}}
						p={2}
					>
						{breadcrumb.name}
					</Text>
				);
				const breadcrumbContent = breadcrumb.noLink ? (
					breadcrumbName
				) : (
					<BreadcrumbLink
						to={breadcrumb.url}
						key={breadcrumb.id}
						data-testid={breadcrumb.dataTestId}
						onClick={breadcrumb.onClick}
					>
						{breadcrumbName}
					</BreadcrumbLink>
				);
				return (
					<Flex align="center" key={index}>
						{(separator.skipFirst ? !isFirstBreadcrumb : true) && (
							<Icon
								key={index}
								variant={separator.icon ?? DEFAULT_SEPARATOR_ICON}
								size="vlg"
							/>
						)}
						{breadcrumb.element ? breadcrumb.element : breadcrumbContent}
					</Flex>
				);
			})}
		</BreadcrumbsWrapper>
	);
};

export const BreadcrumbLink = styled(NavLink)`
	display: flex;
	align-items: center;
	color: inherit;
`;

export const BreadcrumbsWrapper = styled(Flex)<
	FlexProps & { $textColor?: string; $activeItemColor?: string }
>`
	color: ${({ theme, $activeItemColor }) => $activeItemColor ?? theme.v3.colors.dark[8]};

	& > *:not(:last-child) {
		color: ${({ theme, $textColor }) => $textColor ?? theme.v3.colors.dark[0]};

		${BreadcrumbLink} {
			cursor: pointer;
		}
	}

	& > *:last-child ${BreadcrumbLink} {
		cursor: default;
	}
`;

const Memoized = memo(Breadcrumbs);

export { Memoized as Breadcrumbs };
